import * as React from "react";
import { useRef, useState } from "react";
import { WorkflowListItem } from "./types";
import { toShortDate } from "@/helpers/dates";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryGrayButton,
  Modal
} from "@velaro/velaro-component-lib";
import { Toggle } from "@/components/Toggle";
import IconCopy from "@/icons/IconCopy";
import IconEdit1 from "@/icons/IconEdit1";
import IconTrash from "@/icons/IconTrash";
import Tooltip from "@/components/Tooltip";
import { useNavigate } from "react-router-dom";

export function WorkflowItem({
  item,
  isLast,
  isSelected,
  onSelect,
  toggle,
  remove,
  duplicate,
  onDetach,
  onArchive,
  onUnarchive,
  onRename,
  linkedCount,
  renderStatusToggleOnly,
  renderActionsOnly,
  firstCellOverride,
  trRef,
  trStyle,
}: {
  item: WorkflowListItem;
  isLast: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  toggle: () => void | Promise<void>;
  remove: () => void | Promise<void>;
  duplicate?: () => void | Promise<void>;
  onDetach?: () => void;
  onArchive?: (replacedById?: number) => void;
  onUnarchive?: () => void;
  onRename?: (id: number, newName: string) => void;
  linkedCount?: number;
  /** When true, renders only the status toggle (no <tr> wrapper). */
  renderStatusToggleOnly?: boolean;
  /** When true, renders only the action bar (no <tr> wrapper). */
  renderActionsOnly?: boolean;
  /** When provided, replaces the first <td> (checkbox or drag handle). */
  firstCellOverride?: React.ReactNode;
  /** Forwarded ref for the <tr> element (used for DnD sortable). */
  trRef?: React.Ref<HTMLTableRowElement>;
  /** Inline style forwarded to <tr> (used for DnD sortable transforms). */
  trStyle?: React.CSSProperties;
}) {
  const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false);
  const [showConfirmDetachModal, setShowConfirmDetachModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [editingName, setEditingName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastModified = new Date(item.lastModified || "");
  const dateCreated = item.dateCreated ? new Date(item.dateCreated) : null;
  const className = isLast ? "" : "border-b border-slate-200";
  const navigate = useNavigate();

  const isArchived = item.workflowStatus === "deprecated";

  function commitRename() {
    const trimmed = editingName?.trim();
    if (trimmed && trimmed !== item.name && onRename) {
      onRename(item.id, trimmed);
    }
    setEditingName(null);
  }

  function renderActionBar() {
    return (
      <div className="flex flex-row gap-4 items-center text-slate-400">
        <Tooltip text="Edit" direction="top" minWidth="50px">
          <div
            className="hover:cursor-pointer hover:text-primary"
            onClick={() => navigate(`${item.id}`)}
          >
            <IconEdit1 />
          </div>
        </Tooltip>
        {!item.templateId && (
          <Tooltip text="Copy" direction="top" minWidth="50px">
            <div
              className="hover:cursor-pointer hover:text-primary"
              onClick={() => {
                if (duplicate) duplicate();
              }}
            >
              <IconCopy />
            </div>
          </Tooltip>
        )}
        {item.templateId && onDetach && (
          <Tooltip text="Detach from template" direction="top" minWidth="120px">
            <div
              className="hover:cursor-pointer hover:text-amber-500"
              onClick={() => setShowConfirmDetachModal(true)}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18.84 12.25l1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71" />
                <path d="M5.17 11.75l-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71" />
                <line x1="8" y1="2" x2="8" y2="5" />
                <line x1="2" y1="8" x2="5" y2="8" />
                <line x1="16" y1="19" x2="16" y2="22" />
                <line x1="19" y1="16" x2="22" y2="16" />
              </svg>
            </div>
          </Tooltip>
        )}
        {!isArchived && onArchive && (
          <Tooltip text="Archive workflow" direction="top" minWidth="120px">
            <div
              className="hover:cursor-pointer hover:text-amber-500"
              onClick={() => setShowArchiveModal(true)}
              aria-label="Archive workflow"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>
            </div>
          </Tooltip>
        )}
        {isArchived && onUnarchive && (
          <Tooltip text="Unarchive (restores disabled)" direction="top" minWidth="160px">
            <div
              className="hover:cursor-pointer hover:text-green-600"
              onClick={onUnarchive}
              aria-label="Unarchive workflow"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 .49-4" />
              </svg>
            </div>
          </Tooltip>
        )}
        <Tooltip text="Delete" direction="top" minWidth="60px">
          <div
            className="hover:cursor-pointer hover:text-primary"
            onClick={() => setShowConfirmRemoveModal(true)}
          >
            <IconTrash />
          </div>
        </Tooltip>
      </div>
    );
  }

  if (renderStatusToggleOnly) {
    return (
      <div className="flex items-center">
        <Toggle label={item.enabled ? "Published" : "Draft"} toggled={item.enabled} changeToggle={toggle} />
      </div>
    );
  }

  if (renderActionsOnly) {
    return (
      <div className="relative flex justify-center">
        {renderActionBar()}
        <Modal show={showConfirmRemoveModal}>
          <ModalHeader title="Confirm Remove Workflow" onClose={() => setShowConfirmRemoveModal(false)} />
          <ModalBody>
            <div className="m-4">
              {item.isTemplate && typeof linkedCount === "number" && linkedCount > 0
                ? `This template has ${linkedCount} linked workflow(s). Detach them first before deleting.`
                : "Are you sure you want to remove this workflow?"}
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="mx-4 my-2 flex justify-between items-center">
              <SecondaryGrayButton label="Cancel" onClick={() => setShowConfirmRemoveModal(false)} />
              {!(item.isTemplate && typeof linkedCount === "number" && linkedCount > 0) && (
                <PrimaryBlueButton label="Remove" onClick={() => { setShowConfirmRemoveModal(false); remove(); }} />
              )}
            </div>
          </ModalFooter>
        </Modal>
        <Modal show={showConfirmDetachModal}>
          <ModalHeader title="Detach from template?" onClose={() => setShowConfirmDetachModal(false)} />
          <ModalBody>
            <div className="m-4">
              This workflow will no longer receive updates from the template. It will keep its current structure and become independently editable.
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="mx-4 my-2 flex justify-between items-center">
              <SecondaryGrayButton label="Cancel" onClick={() => setShowConfirmDetachModal(false)} />
              <PrimaryBlueButton label="Detach" onClick={() => { setShowConfirmDetachModal(false); if (onDetach) onDetach(); }} />
            </div>
          </ModalFooter>
        </Modal>
        <Modal show={showArchiveModal}>
          <ModalHeader title="Archive workflow?" onClose={() => setShowArchiveModal(false)} />
          <ModalBody>
            <div className="m-4 flex flex-col gap-3">
              <p className="text-sm text-slate-600">Archiving <strong>{item.name}</strong> will <strong>disable it immediately</strong> — it will stop running.</p>
              <p className="text-xs text-slate-400">Archived workflows are hidden from the Active view. You can find and restore them under the Archived filter.</p>
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="mx-4 my-2 flex justify-between items-center">
              <SecondaryGrayButton label="Cancel" onClick={() => setShowArchiveModal(false)} />
              <PrimaryBlueButton label="Archive" onClick={() => { setShowArchiveModal(false); if (onArchive) onArchive(); }} />
            </div>
          </ModalFooter>
        </Modal>
      </div>
    );
  }

  return (
    <tr ref={trRef} style={trStyle} className={`${className} h-12 hover:bg-slate-100 ${isArchived ? "opacity-60" : ""}`}>
      <td>
        {firstCellOverride !== undefined ? firstCellOverride : (
          <div className="flex items-center pl-2">
            <Checkbox checked={!!isSelected} onChange={onSelect} />
          </div>
        )}
      </td>
      <td>
        <div className="flex items-center gap-2">
          {editingName !== null ? (
            <input
              ref={inputRef}
              autoFocus
              value={editingName}
              onChange={e => setEditingName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") setEditingName(null);
              }}
              className="border border-primary rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-0 flex-1"
              style={{ minWidth: 120 }}
            />
          ) : (
            <span
              className="hover:text-primary cursor-pointer hover:underline font-medium"
              onClick={() => navigate(`${item.id}`)}
              onDoubleClick={(e) => { e.stopPropagation(); onRename && setEditingName(item.name ?? ""); }}
              title={onRename ? "Click to open · Double-click to rename" : "Click to open"}
            >
              {item.name}
            </span>
          )}
          {isArchived && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
              Archived
            </span>
          )}
          {isArchived && item.replacedByName && (
            <button
              type="button"
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 whitespace-nowrap"
              onClick={() => navigate(`${item.replacedById}`)}
              aria-label={`Go to replacement: ${item.replacedByName}`}
            >
              {`\u2192 ${item.replacedByName}`}
            </button>
          )}
          {item.category && !isArchived && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 whitespace-nowrap">
              {item.category}
            </span>
          )}
          {item.isTemplate && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 whitespace-nowrap">
              Template
            </span>
          )}
          {item.templateId && item.templateName && (
            <button
              type="button"
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100 whitespace-nowrap"
              onClick={() => navigate(`${item.templateId}`)}
              aria-label={`Go to template: ${item.templateName}`}
            >
              {`\u2197 ${item.templateName}`}
            </button>
          )}
          {item.isTemplate && typeof linkedCount === "number" && linkedCount > 0 && (
            <span className="text-[10px] text-slate-400">
              {linkedCount} linked
            </span>
          )}
        </div>
      </td>
      <td>
        <span className="truncate">{item.createdBy}</span>
      </td>
      <td>
        <div>{dateCreated ? toShortDate(dateCreated) : "—"}</div>
      </td>
      <td>
        <div>{toShortDate(lastModified)}</div>
      </td>
      <td>
        <div className="flex items-center">
          <Toggle
            label={item.enabled ? "Published" : "Draft"}
            toggled={item.enabled}
            changeToggle={toggle}
          />
        </div>
      </td>
      <td>
        <div className="relative flex justify-center">
          {renderActionBar()}
          <Modal show={showConfirmRemoveModal}>
            <ModalHeader
              title="Confirm Remove Workflow"
              onClose={() => setShowConfirmRemoveModal(false)}
            />
            <ModalBody>
              <div className="m-4">
                {item.isTemplate && typeof linkedCount === "number" && linkedCount > 0
                  ? `This template has ${linkedCount} linked workflow(s). Detach them first before deleting.`
                  : "Are you sure you want to remove this workflow?"}
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="mx-4 my-2 flex justify-between items-center">
                <SecondaryGrayButton
                  label="Cancel"
                  onClick={() => setShowConfirmRemoveModal(false)}
                />
                {!(item.isTemplate && typeof linkedCount === "number" && linkedCount > 0) && (
                  <PrimaryBlueButton
                    label="Remove"
                    onClick={() => {
                      setShowConfirmRemoveModal(false);
                      remove();
                    }}
                  />
                )}
              </div>
            </ModalFooter>
          </Modal>
          <Modal show={showConfirmDetachModal}>
            <ModalHeader
              title="Detach from template?"
              onClose={() => setShowConfirmDetachModal(false)}
            />
            <ModalBody>
              <div className="m-4">
                This workflow will no longer receive updates from the template. It will keep its
                current structure and become independently editable.
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="mx-4 my-2 flex justify-between items-center">
                <SecondaryGrayButton
                  label="Cancel"
                  onClick={() => setShowConfirmDetachModal(false)}
                />
                <PrimaryBlueButton
                  label="Detach"
                  onClick={() => {
                    setShowConfirmDetachModal(false);
                    if (onDetach) onDetach();
                  }}
                />
              </div>
            </ModalFooter>
          </Modal>

          <Modal show={showArchiveModal}>
            <ModalHeader
              title="Archive workflow?"
              onClose={() => setShowArchiveModal(false)}
            />
            <ModalBody>
              <div className="m-4 flex flex-col gap-3">
                <p className="text-sm text-slate-600">
                  Archiving <strong>{item.name}</strong> will <strong>disable it immediately</strong> — it will stop running.
                </p>
                <p className="text-xs text-slate-400">
                  Archived workflows are hidden from the Active view. You can find and restore them under the Archived filter.
                </p>
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="mx-4 my-2 flex justify-between items-center">
                <SecondaryGrayButton
                  label="Cancel"
                  onClick={() => setShowArchiveModal(false)}
                />
                <PrimaryBlueButton
                  label="Archive"
                  onClick={() => {
                    setShowArchiveModal(false);
                    if (onArchive) onArchive();
                  }}
                />
              </div>
            </ModalFooter>
          </Modal>
        </div>
      </td>
    </tr>
  );
}

export function Checkbox(props: React.HTMLProps<HTMLInputElement>) {
  return (
    <input
      className="w-4 h-4 rounded border border-slate-300 accent-primary cursor-pointer"
      {...props}
      type="checkbox"
    />
  );
}
